/****************************************************
 * Module Name:  kapanLossListReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useState } from "react";
import Utility from "../../utility/utility";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KapanlosslistModal from "./kapanlosslistModal";
import { v4 as uuidv4 } from "uuid";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import RemarkModal from "../common/remarkModal";
import NoPermission from "../common/noPermission";
import { useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";

function TotalStockComponent({ data }) {
    const makeData = (data) => {
        if (!Array.isArray(data)) return;
        let totaldata = data.length;
        let weight = 0;
        let amount = 0;
        let packets = 0;
        data.forEach((item) => {
            weight += item.totalweight;
            amount += item.totalloss;
            packets += item.totalpackets;
        });


        return [
            totaldata,
            weight,
            amount,
            packets
        ];
    };

    const [
        totaldata,
        weight,
        amount,
        packets
    ] = makeData(data);

    return (
        <TableContainer component={Paper} sx={{ m: "15px 15px 0 0" }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                <TableHead>
                    <TableRow
                        sx={{
                            "& .css-12q85cb-MuiTableCell-root": {
                                fontWeight: 600,
                                backgroundColor: "black",
                                color: "white",
                            },
                        }}
                    >
                        <TableCell align="center">Total Data</TableCell>
                        <TableCell align="center">Total Weight</TableCell>
                        <TableCell align="center">Total Loss</TableCell>
                        <TableCell align="center">Total Packets</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell align="center">{totaldata}</TableCell>
                        <TableCell align="center">
                            {weight ? weight.toFixed(2) : 0}
                        </TableCell>
                        <TableCell align="center">
                            {amount ? amount.toFixed(2) : 0}
                        </TableCell>
                        <TableCell align="center">
                            {packets}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const KapanLossListReport = () => {
    const [data, setData] = useState([]);
    const [packetsIds, setPacketsIds] = useState([]);
    const [packetLoader, setPacketLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [showKapanlossDetailModal, setShowKapanLossDetailModal] = useState(false);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [access, setAccess] = useState(false);
    const [showInlineRemark, setShowInlineRemark] = useState(true);
    const [sourcedata, setSourcedataData] = useState([]);

    useEffect(() => {
        PageTitle("MANUFACTURING_REPORT_LOSS_KAPANLOSS");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_REPORT_LOSS_KAPANLOSS
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const handleSearch = (packetsIds) => {
        setPacketLoader(true);
        ReportProxy.GetMultipleLossByKapan(
            packetsIds.split(/[ ,]+/).map(Number),
            (response) => {
                if (response) {
                    let data = response
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = uuidv4();
                    }
                    setData(data);
                    setPacketLoader(false);
                } else {
                    setPacketLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };

    const handleClose = () => {
        setShowKapanLossDetailModal(false);
        setShowRemarkModal(false)
    };

    const handleClick = (params) => {
        setKapanId(params.row.kapanid)
        setShowKapanLossDetailModal(true)
    }

    const handleRemarkModal = (params) => {
        setSourcedataData(params)
        setShowRemarkModal(true)
    }

    const columnName = [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "totalweight",
            headerName: "TOTAL WEIGHT",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totalloss",
            headerName: "TOTAL LOSS",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totaluser",
            headerName: "TOTAL USER",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totalpackets",
            headerName: "TOTAL PACKETS",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            renderCell: (params) => (
                params.row.remarks === null || params.row.lossremarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>
                            <table style={{ border: '1pt solid black' }}>
                                <tr style={{ border: '1pt solid black' }}>
                                    <th style={{ border: '1pt solid black' }}>Loss Category</th>
                                    <th style={{ border: '1pt solid black' }}>Loss Remarks</th>
                                </tr>
                                {
                                    Utility.GetRemark(params.row.remarks).map((iRow) =>
                                        <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                            <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                            <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                        </tr>
                                    )
                                }
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.remarks)
                            }}
                        >
                            Show Loss Remark
                        </Button>
            ),
        },
    ];

    const rows = data;
    const columns = columnName;

    return (
        <div >
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        {showKapanlossDetailModal ? (
                            <KapanlosslistModal
                                show={showKapanlossDetailModal}
                                kapan={kapanId}
                                close={handleClose}
                            />
                        ) : null}
                        {showRemarkModal ? (
                            <RemarkModal
                                show={showRemarkModal}
                                sourceData={sourcedata}
                                handleClose={handleClose}
                            />
                        ) : null}
                        {showKapanDetailModal ? (
                            <KapanHistoryModal
                                show={showKapanDetailModal}
                                kapanId={kapanId}
                                close={() => setShowKapanDetailModal(false)}
                            />
                        ) : null}
                        <Box display={"flex"} flexDirection="row" width={"70vw"} mt={"20px"} mb={1}>
                            <Box display={"flex"} width="100%">
                                <Typography variant="h6" width="125px" mt={2}>
                                    Kapan Ids
                                </Typography>
                                <TextField
                                    className="picker"
                                    type={"number"}
                                    id="outlined-multiline-static"
                                    label="Enter Kapan ID"
                                    placeholder="Comma seperated Kapan Ids"
                                    multiline
                                    fullWidth
                                    rows={2}
                                    value={packetsIds}
                                    onChange={(e) =>
                                        setPacketsIds(Utility.ConvertNewLineToComma(e.target.value))
                                    }
                                />
                            </Box>
                            <LoadingButton
                                variant="contained"
                                size="large"
                                onClick={() => handleSearch(packetsIds)}
                                loading={packetLoader}
                                loadingPosition="start"
                                startIcon={<SearchOutlinedIcon size="large" />}
                                sx={{ mt: 1, alignSelf: "center", ml: 2 }}
                                disabled={packetsIds.length === 0}
                            >
                                Search
                            </LoadingButton>
                            <FormGroup sx={{ mt: 1, alignSelf: "center", ml: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            disabled={packetLoader}
                                            checked={showInlineRemark}
                                            onChange={() => setShowInlineRemark(!showInlineRemark)}
                                        />
                                    }
                                    label="Inline Loss Remark"
                                />
                            </FormGroup>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                height: "80vh",
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                getRowHeight={() => "auto"}
                                slots={{ toolbar: GridToolbar }}
                                rows={rows}
                                columns={columns}
                                onRowClick={handleClick}
                                getRowId={(row) => row.id}
                                loading={packetLoader}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    sorting: {
                                        sortModel: [{ field: "updatedat", sort: "desc" }],
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {packetLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(packetsIds)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        <Box height={100}>
                            <TotalStockComponent data={data} />
                        </Box>
                    </Box>
                )
            }
        </div>
    );
};

export default KapanLossListReport;
